/**
 * Flagged stretches as the reader uses them: brackets in the right gutter for the focused channels, the
 * previous/next flagged stretch (h / H), the inspector's "flagged stretches containing this point".
 * ED-13: the engine never lists the withheld channels' entries (`hotspots.hotspots` has none), so nothing here can
 * bracket them.
 */
import type { ChannelId } from '../../engine/channels/types.ts';
import type { Hotspot } from '../../engine/ensemble/hotspots.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { READER } from '../strings-reader.ts';

const byChannel = new WeakMap<ViewModel, Map<ChannelId, Hotspot[]>>();

/** Entries of one channel, in the engine's order. */
export function entriesOf(vm: ViewModel, id: ChannelId): Hotspot[] {
	let m = byChannel.get(vm);
	if (!m) {
		m = new Map();
		for (const h of vm.ensemble.hotspots.hotspots) {
			const l = m.get(h.channel);
			if (l) l.push(h);
			else m.set(h.channel, [h]);
		}
		byChannel.set(vm, m);
	}
	return m.get(id) ?? [];
}

const sortedMemo = new WeakMap<ViewModel, Map<string, Hotspot[]>>();

/** Entries of the given channels (all channels when `ids` is empty) sorted by first paragraph, then last, then channel. */
export function sortedFlagged(vm: ViewModel, ids: readonly ChannelId[]): Hotspot[] {
	let m = sortedMemo.get(vm);
	if (!m) {
		m = new Map();
		sortedMemo.set(vm, m);
	}
	const key = [...ids].join(',');
	const hit = m.get(key);
	if (hit) return hit;
	const list =
		ids.length === 0 ? [...vm.ensemble.hotspots.hotspots] : ids.flatMap((id) => entriesOf(vm, id));
	list.sort(
		(x, y) =>
			x.paraStart - y.paraStart ||
			x.paraEnd - y.paraEnd ||
			(x.channel < y.channel ? -1 : x.channel > y.channel ? 1 : 0)
	);
	m.set(key, list);
	return list;
}

/** "hotspot", "elevated", "single paragraph" or "chapter": the label a bracket carries after the channel ID (UX-MAP-09). */
export function flagWord(h: Pick<Hotspot, 'kind' | 'flag'>): string {
	if (h.kind === 'spike') return READER.kindWord.spike;
	if (h.kind === 'unit') return READER.kindWord.unit;
	return h.flag === 'hotspot' ? READER.kindWord.hotspot : READER.kindWord.elevated;
}

/** First entry in `list` (sorted by paraStart) that starts after paragraph `p` (strictly), or −1. */
export function nextStart(list: readonly Hotspot[], p: number): number {
	let lo = 0;
	let hi = list.length;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if ((list[mid] as Hotspot).paraStart > p) hi = mid;
		else lo = mid + 1;
	}
	return lo < list.length ? lo : -1;
}

/** Last entry that starts before paragraph `p` (strictly), or −1. */
export function prevStart(list: readonly Hotspot[], p: number): number {
	let lo = 0;
	let hi = list.length;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if ((list[mid] as Hotspot).paraStart >= p) hi = mid;
		else lo = mid + 1;
	}
	return lo - 1;
}

export interface BracketCell {
	slot: number;
	first: boolean;
	last: boolean;
	/** Labels printed on the row where the stretch starts. */
	labels: string[];
}

/**
 * Bracket cells for paragraphs [a, b): one cell per (paragraph, slot), merging entries of the same channel that
 * overlap there. `slotOf` maps a focused channel to its slot.
 */
export function bracketsFor(
	list: readonly Hotspot[],
	slotOf: ReadonlyMap<ChannelId, number>,
	a: number,
	b: number
): Map<number, BracketCell[]> {
	const out = new Map<number, BracketCell[]>();
	for (const h of list) {
		if (h.paraEnd <= a || h.paraStart >= b) continue;
		const slot = slotOf.get(h.channel);
		if (slot === undefined) continue;
		const from = Math.max(h.paraStart, a);
		const to = Math.min(h.paraEnd, b);
		for (let p = from; p < to; p++) {
			let cells = out.get(p);
			if (!cells) {
				cells = [];
				out.set(p, cells);
			}
			let cell = cells.find((c) => c.slot === slot);
			if (!cell) {
				cell = { slot, first: false, last: false, labels: [] };
				cells.push(cell);
			}
			if (p === h.paraStart) {
				cell.first = true;
				cell.labels.push(READER.bracketLabel(h.channel, flagWord(h)));
			}
			if (p === h.paraEnd - 1) cell.last = true;
		}
	}
	return out;
}
